import React, { useState, useEffect } from 'react';
import { Group, User } from '../types';
import { 
  getAvailableMembers,
  loadGroupsFromStorage,
  saveGroupsToStorage,
  loadUsersFromStorage,
  saveUsersToStorage,
  generateUniqueId
} from '../utils/dataProcessor';

const Settings: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [availableMembers, setAvailableMembers] = useState<{ provider: string; identifier: string }[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<{ provider: string; identifier: string }[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<{ provider: string; identifier: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGroups();
    loadUsers();
    loadAvailableMembers();
  }, []);

  const loadGroups = () => {
    const savedGroups = loadGroupsFromStorage();
    setGroups(savedGroups);
  };

  const loadUsers = () => {
    const savedUsers = loadUsersFromStorage();
    setUsers(savedUsers);
  };

  const loadAvailableMembers = () => {
    const uniqueMembers = getAvailableMembers();
    setAvailableMembers(uniqueMembers);
  };

  const generateUniqueGroupId = () => {
    return generateUniqueId('group');
  };

  const generateUniqueUserId = () => {
    return generateUniqueId('user');
  };

  const saveGroups = (newGroups: Group[]) => {
    try {
      saveGroupsToStorage(newGroups);
      setGroups(newGroups);
      setError(null);
    } catch (err) {
      console.error('Failed to save groups:', err);
      setError('グループの保存に失敗しました。もう一度お試しください。');
      throw err;
    }
  };

  const saveUsers = (newUsers: User[]) => {
    try {
      saveUsersToStorage(newUsers);
      setUsers(newUsers);
      setError(null);
    } catch (err) {
      console.error('Failed to save users:', err);
      setError('ユーザーの保存に失敗しました。もう一度お試しください。');
      throw err;
    }
  };

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setNewGroupName('');
    setSelectedMembers([]);
    setError(null);
    setShowGroupModal(true);
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setNewUserName('');
    setSelectedProviders([]);
    setError(null);
    setShowUserModal(true);
  };

  const handleEditGroup = (group: Group) => {
    setEditingGroup(group);
    setNewGroupName(group.name);
    setSelectedMembers([...group.members]);
    setError(null);
    setShowGroupModal(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setNewUserName(user.name);
    setSelectedProviders([...user.providers]);
    setError(null);
    setShowUserModal(true);
  };

  const handleDeleteGroup = (groupId: string) => {
    if (window.confirm('このグループを本当に削除しますか？')) {
      const newGroups = groups.filter(g => g.id !== groupId);
      saveGroups(newGroups);
    }
  };

  const handleDeleteUser = (userId: string) => {
    if (window.confirm('このユーザーを本当に削除しますか？')) {
      const newUsers = users.filter(u => u.id !== userId);
      saveUsers(newUsers);
    }
  };

  const handleSaveGroup = async () => {
    if (isLoading) return;
    
    if (!newGroupName.trim()) {
      setError('グループ名を入力してください。');
      return;
    }
    
    if (selectedMembers.length === 0) {
      setError('少なくとも1人のメンバーを選択してください。');
      return;
    }

    const isDuplicateName = groups.some(group => 
      group.name.toLowerCase() === newGroupName.trim().toLowerCase() &&
      group.id !== editingGroup?.id
    );
    
    if (isDuplicateName) {
      setError('すでに存在するグループ名です。');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const newGroup: Group = {
        id: editingGroup?.id || generateUniqueGroupId(),
        name: newGroupName.trim(),
        members: [...selectedMembers],
        totalCost: 0
      };

      let newGroups: Group[];
      if (editingGroup) {
        newGroups = groups.map(g => g.id === editingGroup.id ? newGroup : g);
      } else {
        newGroups = [...groups, newGroup];
      }

      saveGroups(newGroups);
      
      setShowGroupModal(false);
      setNewGroupName('');
      setSelectedMembers([]);
      setEditingGroup(null);
      
    } catch (err) {
      // 에러는 saveGroups에서 이미 처리됨
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveUser = async () => {
    if (isLoading) return;
    
    if (!newUserName.trim()) {
      setError('ユーザー名を入力してください。');
      return;
    }

    if (selectedProviders.length === 0) {
      setError('少なくとも1つのプロバイダーを選択してください。');
      return;
    }

    const isDuplicateName = users.some(user => 
      user.name.toLowerCase() === newUserName.trim().toLowerCase() &&
      user.id !== editingUser?.id
    );
    
    if (isDuplicateName) {
      setError('すでに存在するユーザー名です。');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const newUser: User = {
        id: editingUser?.id || generateUniqueUserId(),
        name: newUserName.trim(),
        providers: [...selectedProviders],
        totalCost: 0
      };

      let newUsers: User[];
      if (editingUser) {
        newUsers = users.map(u => u.id === editingUser.id ? newUser : u);
      } else {
        newUsers = [...users, newUser];
      }

      saveUsers(newUsers);
      
      setShowUserModal(false);
      setNewUserName('');
      setSelectedProviders([]);
      setEditingUser(null);
      
    } catch (err) {
      // 에러는 saveUsers에서 이미 처리됨
    } finally {
      setIsLoading(false);
    }
  };

  const handleMemberToggle = (member: { provider: string; identifier: string }) => {
    const isSelected = selectedMembers.some(
      m => m.provider === member.provider && m.identifier === member.identifier
    );

    if (isSelected) {
      setSelectedMembers(selectedMembers.filter(
        m => !(m.provider === member.provider && m.identifier === member.identifier)
      ));
    } else {
      setSelectedMembers([...selectedMembers, member]);
    }
  };

  const handleProviderToggle = (provider: { provider: string; identifier: string }) => {
    const isSelected = selectedProviders.some(
      p => p.provider === provider.provider && p.identifier === provider.identifier
    );

    if (isSelected) {
      setSelectedProviders(selectedProviders.filter(
        p => !(p.provider === provider.provider && p.identifier === provider.identifier)
      ));
    } else {
      setSelectedProviders([...selectedProviders, provider]);
    }
  };

  const isMemberSelected = (member: { provider: string; identifier: string }) => {
    return selectedMembers.some(
      m => m.provider === member.provider && m.identifier === member.identifier
    );
  };

  const isProviderSelected = (provider: { provider: string; identifier: string }) => {
    return selectedProviders.some(
      p => p.provider === provider.provider && p.identifier === provider.identifier
    );
  };

  const isMemberInOtherGroup = (member: { provider: string; identifier: string }) => {
    return groups.some(group => 
      group.id !== editingGroup?.id && 
      group.members.some(m => m.provider === member.provider && m.identifier === member.identifier)
    );
  };

  const isProviderInOtherUser = (provider: { provider: string; identifier: string }) => {
    return users.some(user => 
      user.id !== editingUser?.id && 
      user.providers.some(p => p.provider === provider.provider && p.identifier === provider.identifier)
    );
  };

  const handleCloseModal = () => {
    setShowGroupModal(false);
    setNewGroupName('');
    setSelectedMembers([]);
    setEditingGroup(null);
    setError(null);
    setIsLoading(false);
  };

  const handleCloseUserModal = () => {
    setShowUserModal(false);
    setNewUserName('');
    setSelectedProviders([]);
    setEditingUser(null);
    setError(null);
    setIsLoading(false);
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">設定</h1>
          <p className="text-gray-600 mt-2">グループ、ユーザー、システム構成を管理します</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">グループ管理</h2>
          <button
            onClick={handleCreateGroup}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            グループ作成
          </button>
        </div>

        {groups.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">作成されたグループがありません</h3>
            <p className="text-gray-500">最初のグループを作成してコストデータを整理します</p>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{group.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {group.members.length} メンバー
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {group.members.map((member, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {member.provider}: {member.identifier}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditGroup(group)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      削除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">ユーザー管理</h2>
          <button
            onClick={handleCreateUser}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            ユーザー作成
          </button>
        </div>

        {users.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">作成されたユーザーがいません</h3>
            <p className="text-gray-500">最初のユーザーを作成してコストデータを整理します</p>
          </div>
        ) : (
          <div className="space-y-4">
            {users.map((user) => (
              <div key={user.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">{user.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {user.providers.length} プロバイダー
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {user.providers.map((provider, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                        >
                          {provider.provider}: {provider.identifier}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="text-green-600 hover:text-green-800 text-sm font-medium"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      削除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingGroup ? 'グループ編集' : '新規グループ作成'}
              </h3>
            </div>
            
            <div className="p-6 overflow-y-auto">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  グループ名
                </label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => {
                    setNewGroupName(e.target.value);
                    if (error) setError(null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="グループ名を入力してください"
                  disabled={isLoading}
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  メンバー選択 ({selectedMembers.length} 件選択)
                </label>
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                  {availableMembers.map((member, index) => {
                    const isSelected = isMemberSelected(member);
                    const isInOtherGroup = isMemberInOtherGroup(member);
                    const isDisabled = isLoading || (isInOtherGroup && !isSelected);

                    return (
                      <div
                        key={`${member.provider}-${member.identifier}`}
                        className={`flex items-center p-3 border-b border-gray-100 last:border-b-0 ${
                          isDisabled ? 'opacity-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => {
                            handleMemberToggle(member);
                            if (error) setError(null);
                          }}
                          className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {member.identifier}
                          </div>
                          <div className="text-xs text-gray-500">
                            {member.provider}
                            {isInOtherGroup && (
                              <span className="ml-2 text-orange-600">(すでに他のグループに属しています)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCloseModal}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                キャンセル
              </button>
              <button
                onClick={handleSaveGroup}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isLoading && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isLoading ? '保存中...' : (editingGroup ? 'グループ更新' : 'グループ作成')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingUser ? 'ユーザー編集' : '新規ユーザー作成'}
              </h3>
            </div>
            
            <div className="p-6 overflow-y-auto">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ユーザー名
                </label>
                <input
                  type="text"
                  value={newUserName}
                  onChange={(e) => {
                    setNewUserName(e.target.value);
                    if (error) setError(null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="ユーザー名を入力してください"
                  disabled={isLoading}
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  プロバイダー選択 ({selectedProviders.length} 件選択)
                </label>
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                  {availableMembers.map((provider, index) => {
                    const isSelected = isProviderSelected(provider);
                    const isInOtherUser = isProviderInOtherUser(provider);
                    const isDisabled = isLoading || (isInOtherUser && !isSelected);

                    return (
                      <div
                        key={`${provider.provider}-${provider.identifier}`}
                        className={`flex items-center p-3 border-b border-gray-100 last:border-b-0 ${
                          isDisabled ? 'opacity-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => {
                            handleProviderToggle(provider);
                            if (error) setError(null);
                          }}
                          className="mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {provider.identifier}
                          </div>
                          <div className="text-xs text-gray-500">
                            {provider.provider}
                            {isInOtherUser && (
                              <span className="ml-2 text-orange-600">(すでに他のユーザーに割り当てられています)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCloseUserModal}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                キャンセル
              </button>
              <button
                onClick={handleSaveUser}
                disabled={isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isLoading && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isLoading ? '保存中...' : (editingUser ? 'ユーザー更新' : 'ユーザー作成')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;